import { supabase } from '../lib/supabase';

export const aiReviewService = {
  // Get all protocols with optional status filter
  async getAll(status = null) {
    try {
      let query = supabase?.from('ai_review_protocols')?.select(`
          *,
          requested_by_profile:user_profiles!ai_review_protocols_requested_by_fkey(id, email, first_name, last_name),
          reviewed_by_profile:user_profiles!ai_review_protocols_reviewed_by_fkey(id, email, first_name, last_name)
        `)?.order('priority', { ascending: false })?.order('created_at', { ascending: false });
      
      if (status) {
        query = query?.eq('status', status);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      return data?.map(protocol => ({
        id: protocol?.id,
        actionType: protocol?.action_type,
        actionDescription: protocol?.action_description,
        associatedPromptIds: protocol?.associated_prompt_ids,
        validationRules: protocol?.validation_rules,
        status: protocol?.status,
        priority: protocol?.priority,
        requestedBy: protocol?.requested_by,
        requestedByProfile: protocol?.requested_by_profile,
        reviewedBy: protocol?.reviewed_by,
        reviewedByProfile: protocol?.reviewed_by_profile,
        approvalNotes: protocol?.approval_notes,
        createdAt: protocol?.created_at,
        updatedAt: protocol?.updated_at,
        reviewedAt: protocol?.reviewed_at
      }));
    } catch (error) {
      console.error('Error fetching protocols:', error);
      throw error;
    }
  },

  // Get protocol by ID with checkpoints
  async getById(id) {
    try {
      const { data: protocol, error: protocolError } = await supabase?.from('ai_review_protocols')?.select(`
          *,
          requested_by_profile:user_profiles!ai_review_protocols_requested_by_fkey(id, email, first_name, last_name),
          reviewed_by_profile:user_profiles!ai_review_protocols_reviewed_by_fkey(id, email, first_name, last_name)
        `)?.eq('id', id)?.single();
      
      if (protocolError) throw protocolError;
      
      const { data: checkpoints, error: checkpointsError } = await supabase?.from('validation_checkpoints')?.select('*')?.eq('protocol_id', id)?.order('created_at', { ascending: true });
      
      if (checkpointsError) throw checkpointsError;
      
      return {
        id: protocol?.id,
        actionType: protocol?.action_type,
        actionDescription: protocol?.action_description,
        associatedPromptIds: protocol?.associated_prompt_ids,
        validationRules: protocol?.validation_rules,
        status: protocol?.status,
        priority: protocol?.priority,
        requestedBy: protocol?.requested_by,
        requestedByProfile: protocol?.requested_by_profile,
        reviewedBy: protocol?.reviewed_by,
        reviewedByProfile: protocol?.reviewed_by_profile,
        approvalNotes: protocol?.approval_notes,
        createdAt: protocol?.created_at,
        updatedAt: protocol?.updated_at,
        reviewedAt: protocol?.reviewed_at,
        checkpoints: checkpoints?.map(cp => ({
          id: cp?.id,
          checkpointName: cp?.checkpoint_name,
          checkpointType: cp?.checkpoint_type,
          required: cp?.required,
          passed: cp?.passed,
          validationDetails: cp?.validation_details,
          checkedAt: cp?.checked_at
        }))
      };
    } catch (error) {
      console.error('Error fetching protocol by ID:', error);
      throw error;
    }
  },

  // Create new protocol
  async create(protocolData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await supabase?.from('ai_review_protocols')?.insert({
          action_type: protocolData?.actionType,
          action_description: protocolData?.actionDescription,
          associated_prompt_ids: protocolData?.associatedPromptIds || [],
          validation_rules: protocolData?.validationRules || [],
          status: 'pending',
          priority: protocolData?.priority || 0,
          requested_by: user?.id
        })?.select()?.single();
      
      if (error) throw error;
      
      // Create initial validation checkpoints if provided
      if (protocolData?.checkpoints && protocolData?.checkpoints?.length > 0) {
        const checkpointsToInsert = protocolData?.checkpoints?.map(cp => ({
          protocol_id: data?.id,
          checkpoint_name: cp?.checkpointName,
          checkpoint_type: cp?.checkpointType,
          required: cp?.required ?? true,
          validation_details: cp?.validationDetails || {}
        }));
        
        await supabase?.from('validation_checkpoints')?.insert(checkpointsToInsert);
      }
      
      return {
        id: data?.id,
        actionType: data?.action_type,
        actionDescription: data?.action_description,
        status: data?.status,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error creating protocol:', error);
      throw error;
    }
  },

  // Update protocol status (approve, reject, etc.)
  async updateStatus(id, status, approvalNotes = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await supabase?.from('ai_review_protocols')?.update({
          status: status,
          reviewed_by: user?.id,
          reviewed_at: new Date()?.toISOString(),
          approval_notes: approvalNotes
        })?.eq('id', id)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        status: data?.status,
        reviewedAt: data?.reviewed_at
      };
    } catch (error) {
      console.error('Error updating protocol status:', error);
      throw error;
    }
  },

  // Update checkpoint validation status
  async updateCheckpoint(checkpointId, passed, validationDetails = null) {
    try {
      const { data, error } = await supabase?.from('validation_checkpoints')?.update({
          passed: passed,
          checked_at: new Date()?.toISOString(),
          validation_details: validationDetails
        })?.eq('id', checkpointId)?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        passed: data?.passed,
        checkedAt: data?.checked_at
      };
    } catch (error) {
      console.error('Error updating checkpoint:', error);
      throw error;
    }
  },

  // Delete protocol
  async delete(id) {
    try {
      const { error } = await supabase?.from('ai_review_protocols')?.delete()?.eq('id', id);
      
      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting protocol:', error);
      throw error;
    }
  },

  // Get action types
  async getActionTypes() {
    return [
      { value: 'code_generation', label: 'Code Generation' },
      { value: 'data_modification', label: 'Data Modification' },
      { value: 'schema_change', label: 'Schema Change' },
      { value: 'user_action', label: 'User Action' },
      { value: 'system_configuration', label: 'System Configuration' }
    ];
  },

  // Get review statuses
  async getReviewStatuses() {
    return [
      { value: 'pending', label: 'Pending Review' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'requires_modification', label: 'Requires Modification' }
    ];
  }
};